import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const scanline = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  50% { opacity: 0.3; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const shimmer = keyframes`
  0% { 
    background-position: -200% center;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% { 
    background-position: 200% center;
    opacity: 0.3;
  }
`;

const techGridPulse = keyframes`
  0%, 100% { 
    opacity: 0.08;
    transform: scale(1);
  }
  50% { 
    opacity: 0.2;
    transform: scale(1.01);
  }
`;

const fadeInSlide = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    filter: blur(5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const hologramFlicker = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
  75% { opacity: 0.95; }
`;

const energyPulse = keyframes`
  0% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 40px rgba(100, 182, 255, 0.4),
      inset 0 0 30px rgba(100, 182, 255, 0.2);
  }
  100% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
`;

const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px) translateZ(0);
  }
  50% { 
    transform: translateY(-15px) translateZ(10px);
  }
`;

const imageReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9) rotateY(10deg);
    filter: blur(10px) brightness(0.5);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotateY(0deg);
    filter: blur(0) brightness(1);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 40px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  perspective: 1000px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(100, 182, 255, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 50%, rgba(0, 255, 255, 0.02) 0%, transparent 50%),
      linear-gradient(90deg, rgba(100, 182, 255, 0.02) 1px, transparent 1px),
      linear-gradient(rgba(100, 182, 255, 0.02) 1px, transparent 1px);
    background-size: 100% 100%, 100% 100%, 60px 60px, 60px 60px;
    animation: ${techGridPulse} 8s ease-in-out infinite;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, transparent 0%, rgba(0, 10, 20, 0.3) 100%);
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  background: linear-gradient(
    135deg,
    rgba(100, 182, 255, 0.9),
    rgba(0, 255, 255, 0.9),
    rgba(100, 182, 255, 0.9)
  );
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 8px;
  font-size: 2.4rem;
  text-transform: uppercase;
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  font-weight: 300;
  filter: drop-shadow(0 0 30px rgba(100, 182, 255, 0.5));
  animation: ${shimmer} 6s ease-in-out infinite, ${hologramFlicker} 4s ease-in-out infinite;
  z-index: 10;
  
  &::before {
    content: '// AI ENHANCEMENT';
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(100, 182, 255, 0.4);
    font-weight: 300;
    font-size: 0.7rem;
    letter-spacing: 3px;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 0.8),
      rgba(100, 182, 255, 0.8),
      transparent
    );
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  }
`;

const ContentPreview = styled.div`
  max-width: 1600px;
  width: 95%;
  margin: 0 auto;
  margin-top: 140px;
  margin-bottom: 40px;
  padding: 60px 80px 80px;
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 30px;
  background: 
    linear-gradient(
      135deg,
      rgba(10, 25, 45, 0.4) 0%,
      rgba(15, 35, 60, 0.3) 50%,
      rgba(10, 25, 45, 0.4) 100%
    ),
    rgba(0, 0, 0, 0.4);
  box-shadow: 
    0 20px 80px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(100, 182, 255, 0.15),
    inset 0 0 80px rgba(100, 182, 255, 0.03);
  backdrop-filter: blur(40px) saturate(150%);
  position: relative;
  overflow: visible;
  animation: ${energyPulse} 6s ease-in-out infinite;
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 5px 30px rgba(100, 182, 255, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 -5px 30px rgba(100, 182, 255, 0.3);
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(100, 182, 255, 0.05) 30%,
    rgba(0, 255, 255, 0.08) 50%,
    rgba(100, 182, 255, 0.05) 70%,
    transparent 100%
  );
  animation: ${scanline} 8s ease-in-out infinite;
  pointer-events: none;
  z-index: 5;
  filter: blur(1px);
`;

const ImagesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  justify-items: center;
  
  @media (max-width: 1024px) {
    gap: 40px;
    max-width: 700px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    max-width: 400px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 182, 255, 0.2);
  animation: ${fadeInSlide} 0.8s cubic-bezier(0.4, 0, 0.2, 1), ${floatAnimation} 8s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s, 0.8s;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 1),
      rgba(100, 182, 255, 0.8),
      transparent
    );
    z-index: 2;
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.6);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(100, 182, 255, 0.1) 0%,
      transparent 50%,
      rgba(0, 255, 255, 0.1) 100%
    );
    opacity: 0;
    transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(100, 182, 255, 0.5);
    box-shadow: 
      0 25px 70px rgba(100, 182, 255, 0.3),
      0 0 100px rgba(100, 182, 255, 0.2),
      inset 0 0 60px rgba(100, 182, 255, 0.1);
    
    &::after {
      opacity: 1;
    }
  }
`;

const PropertyImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  position: relative;
  filter: brightness(0.95) contrast(1.05);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${imageReveal} 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${ImageWrapper}:hover & {
    filter: brightness(1.05) contrast(1.1);
    transform: scale(1.02);
  }
`;

const ImageLabel = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  padding: 10px 20px;
  border-radius: 10px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  z-index: 3;
  
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  color: rgba(100, 182, 255, 0.9);
  text-transform: uppercase;
  
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.3);
  animation: ${glowPulse} 3s ease-in-out infinite;
`;


const HolographicCorner = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  ${props => {
    switch(props.$position) {
      case 'top-left':
        return 'top: 0; left: 0; border-top: 2px solid; border-left: 2px solid;';
      case 'top-right':
        return 'top: 0; right: 0; border-top: 2px solid; border-right: 2px solid;';
      case 'bottom-left':
        return 'bottom: 0; left: 0; border-bottom: 2px solid; border-left: 2px solid;';
      case 'bottom-right':
        return 'bottom: 0; right: 0; border-bottom: 2px solid; border-right: 2px solid;';
      default:
        return '';
    }
  }}
  border-color: rgba(100, 182, 255, 0.4);
  animation: ${glowPulse} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  pointer-events: none;
  z-index: 15;
  
  &::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background: radial-gradient(circle, rgba(100, 182, 255, 0.8), transparent);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.8);
    ${props => {
      switch(props.$position) {
        case 'top-left':
          return 'top: -3px; left: -3px;';
        case 'top-right':
          return 'top: -3px; right: -3px;';
        case 'bottom-left':
          return 'bottom: -3px; left: -3px;';
        case 'bottom-right':
          return 'bottom: -3px; right: -3px;';
        default:
          return '';
      }
    }}
  }
`;

const TechParticle = styled.div`
  position: absolute;
  width: 3px;
  height: 3px;
  background: radial-gradient(circle, #64B6FF, #00FFFF);
  border-radius: 50%;
  box-shadow: 
    0 0 15px rgba(100, 182, 255, 1),
    0 0 30px rgba(100, 182, 255, 0.5);
  animation: 
    ${floatAnimation} 4s ease-in-out infinite,
    ${glowPulse} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  top: ${props => props.$top}%;
  right: ${props => props.$right}%;
  opacity: 0.7;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(100, 182, 255, 0.3), transparent 70%);
    border-radius: 50%;
    animation: ${glowPulse} 2s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
  }
`;

const PresentationSlide13 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide13', lang);
  const [currentProperty, setCurrentProperty] = useState(1);
  const maxProperties = 5;

  const handleClick = () => {
    if (currentProperty < maxProperties) {
      setCurrentProperty(currentProperty + 1);
    }
  };

  // Define a extensão correta para cada pasta
  const getOriginalExtension = (propertyNumber) => {
    return propertyNumber === 5 ? 'jpeg' : 'jpg';
  };

  return (
    <SlideWrapper onClick={handleClick}>
      <SlideTitle>{t.title}</SlideTitle>
      
      <ContentPreview>
        <ScanLine />
        
        <HolographicCorner $position="top-left" $delay={0} />
        <HolographicCorner $position="top-right" $delay={0.5} />
        <HolographicCorner $position="bottom-left" $delay={1} />
        <HolographicCorner $position="bottom-right" $delay={1.5} />
        
        <TechParticle $top={10} $right={5} $delay={0} />
        <TechParticle $top={20} $right={95} $delay={0.5} />
        <TechParticle $top={35} $right={8} $delay={0.8} />
        <TechParticle $top={45} $right={92} $delay={1.2} />
        <TechParticle $top={60} $right={5} $delay={1.6} />
        <TechParticle $top={75} $right={95} $delay={2} />
        <TechParticle $top={85} $right={10} $delay={2.4} />
        
        <ImagesContainer>
          <ImageWrapper $delay={0}>
            <ImageLabel>{t.original}</ImageLabel>
            <PropertyImage 
              src={`/imoveis/${currentProperty}/1.${getOriginalExtension(currentProperty)}`} 
              alt={`Imóvel ${currentProperty} - Original`}
              key={`${currentProperty}-1`}
            />
          </ImageWrapper>
          
          <ImageWrapper $delay={0.2}>
            <ImageLabel>{t.enhanced}</ImageLabel>
            <PropertyImage 
              src={`/imoveis/${currentProperty}/2.png`} 
              alt={`Imóvel ${currentProperty} - Enhanced`}
              key={`${currentProperty}-2`}
            />
          </ImageWrapper>
        </ImagesContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide13;
